import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { validateAddSource } from '../../lib/addSourceForm';
import { ManagedSource } from '../../types';
import { Bin, Button, Modal, Plus, Spinner } from '../../uikit';

// COM-647 Task 4: one component, two modes (the brief's own wording) rather
// than a separate add/edit dialog - both share the same row shape, the same
// per-row validation (`validateAddSource`, reused verbatim from COM-640's
// AddSourceDialog - see that file's own doc comment for why a real URL
// shape via `isValidSourceUrl` is the right bar here, not the server's
// looser "url is required" guard), and the same Modal/Button chrome. Add
// supports several rows ("+ Add another source", Figma's own batch-add
// affordance) each validated INDEPENDENTLY - one row's bad url does not
// block submitting the others once fixed, and a row's own error clears the
// moment its own url changes, exactly like AddSourceDialog's single field
// already does. Edit is always exactly one row, prefilled from the source
// being edited, with no add/remove controls.
export type SourceFormMode = 'add' | 'edit';

interface SourceFormRow {
  // Local-only React key - never sent over the wire, and never the same as
  // the source's own `id` (edit mode's single row still gets a fresh one on
  // every dialog open, see `buildInitialRows` below) so nothing here can be
  // mistaken for real source identity.
  key: string;
  name: string;
  url: string;
  urlError: string | null;
}

export interface SourceFormAddEntry {
  name?: string;
  url: string;
}

export interface SourceFormEditData {
  name?: string;
  url: string;
}

export interface SourceFormDialogProps {
  open: boolean;
  mode: SourceFormMode;
  /** edit mode only - the row being edited, used to prefill the single row.
   * Ignored in add mode. */
  source?: ManagedSource | null;
  /** true from submit until the caller's own mutation settles - disables
   * every field/button, same contract AddSourceDialog's own `submitting`
   * has. */
  submitting: boolean;
  /** the server's refusal of the last submission (duplicate url, the
   * 100-source cap, "Source not found") - rendered inline in this
   * still-open dialog, same pattern as AddSourceDialog's `refusalError`. */
  refusalError: string | null;
  onClose: () => void;
  onSubmitAdd: (entries: SourceFormAddEntry[]) => void;
  onSubmitEdit: (data: SourceFormEditData) => void;
}

let nextRowKeySeq = 0;
function nextRowKey(): string {
  nextRowKeySeq += 1;
  return `source-form-row-${nextRowKeySeq}`;
}

function emptyRow(): SourceFormRow {
  return { key: nextRowKey(), name: '', url: '', urlError: null };
}

function buildInitialRows(mode: SourceFormMode, source?: ManagedSource | null): SourceFormRow[] {
  if (mode === 'edit' && source) {
    return [{ key: nextRowKey(), name: source.name, url: source.url, urlError: null }];
  }
  return [emptyRow()];
}

export function SourceFormDialog({
  open,
  mode,
  source,
  submitting,
  refusalError,
  onClose,
  onSubmitAdd,
  onSubmitEdit,
}: SourceFormDialogProps) {
  const [rows, setRows] = useState<SourceFormRow[]>(() => buildInitialRows(mode, source));

  // Resets to a fresh row set every time the dialog transitions to open (or
  // edit's own target source changes while open, e.g. the row picked from
  // the table's ActionsMenu changes) - mirrors AddSourceDialog's own
  // `resetForm`-on-close, but keyed on `open`/`mode`/`source` rather than a
  // manual call, since this dialog has no local "just submitted" moment of
  // its own to hook that reset off (`onSubmitAdd`/`onSubmitEdit` are
  // fire-and-forget into the parent, which owns `submitting/refusalError`
  // and decides when to actually close this dialog).
  useEffect(() => {
    if (open) {
      setRows(buildInitialRows(mode, source));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, source?.id]);

  const handleClose = () => {
    if (submitting) {
      return;
    }
    onClose();
  };

  const updateRow = (key: string, patch: Partial<SourceFormRow>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    setRows((current) => [...current, emptyRow()]);
  };

  const removeRow = (key: string) => {
    setRows((current) => (current.length > 1 ? current.filter((row) => row.key !== key) : current));
  };

  const handleSubmit = () => {
    // Every row is validated independently (the brief's own wording) - a
    // single pass collects EVERY row's own error before deciding whether to
    // submit, rather than stopping at the first bad row, so a batch add
    // shows all its problems at once instead of one fix-and-resubmit cycle
    // per bad row.
    let hasError = false;
    const validated = rows.map((row) => {
      const { urlError } = validateAddSource({ name: row.name, url: row.url });
      if (urlError) {
        hasError = true;
      }
      return { ...row, urlError };
    });

    if (hasError) {
      setRows(validated);
      return;
    }

    if (mode === 'add') {
      onSubmitAdd(rows.map((row) => ({ name: row.name.trim(), url: row.url.trim() })));
      return;
    }

    const [row] = rows;
    onSubmitEdit({ name: row.name.trim(), url: row.url.trim() });
  };

  const title = mode === 'add' ? 'Add Source' : 'Edit Source';
  const submitLabel = mode === 'add' ? 'Add' : 'Save';
  const submittingLabel = mode === 'add' ? 'Adding…' : 'Saving…';

  return (
    <Modal
      open={open}
      title={title}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <Spinner sx={{ width: 16, height: 16 }} /> : undefined}
          >
            {submitting ? submittingLabel : submitLabel}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((row, index) => (
          <Box
            key={row.key}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                <TextField
                  autoFocus={index === 0}
                  fullWidth
                  variant="outlined"
                  placeholder="Source name (optional)"
                  value={row.name}
                  onChange={(e) => updateRow(row.key, { name: e.target.value })}
                  disabled={submitting}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="https://example.com"
                  value={row.url}
                  onChange={(e) => updateRow(row.key, { url: e.target.value, urlError: null })}
                  error={!!row.urlError}
                  helperText={row.urlError}
                  disabled={submitting}
                />
              </Box>
              {/* Only add mode ever has more than one row, so the remove
                  control only ever renders there. */}
              {mode === 'add' && rows.length > 1 && (
                <IconButton
                  aria-label={`remove source ${index + 1}`}
                  onClick={() => removeRow(row.key)}
                  disabled={submitting}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <Bin sx={{ width: 16, height: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}

        {mode === 'add' && (
          <Button
            variant="text"
            startIcon={<Plus sx={{ width: 14, height: 14 }} />}
            onClick={addRow}
            disabled={submitting}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add another source
          </Button>
        )}

        {refusalError && (
          <Typography fontSize={14} fontWeight={500} color="error.main">
            {refusalError}
          </Typography>
        )}
      </Box>
    </Modal>
  );
}
